import { useAction, useAtom } from '@reatom/npm-react'
import { memo } from 'react'

import { AsyncStateN } from '@blade/std-lib'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import {
  activePresetIndexAtom,
  tradeSettingsModel,
  tradeSettingsStateAtom,
} from '@root/features/trade/trade-settings'

import { QuickBuyAmount } from './quick-buy-amount'
import { useSpendable } from './spendable'

/**
 * The strip along the bottom: how the connection is, which preset is on, what
 * is held, and the three numbers a quick buy uses.
 *
 * The design draws nine slots. Six of them have a real source and are here.
 * The other three — today's profit, fees paid, wallets tracked — have nothing
 * behind them yet, and a slot that always reads `—` is worse than no slot, so
 * they are left out rather than drawn empty. They are the ones the design marks
 * as the first to go at narrow widths anyway.
 */
export const PulseFooter: React.FC = memo(() => (
  <footer className="foot">
    <Connection />
    <PresetSwitch />
    <Held />

    <div className="footright">
      <QuickBuyAmount section="exploreFeeds" shape="line" />
      <Slippage />
      <Priority />
    </div>
  </footer>
))

/** Green and breathing while the socket answers, red the moment it stops. */
const Connection: React.FC = memo(() => {
  const [dependencies] = useAtom(dependenciesAtom)
  const [latency] = useAtom(dependencies.mainWebsocket.socketLatencyAtom)

  const live = latency !== undefined

  return (
    <div className={live ? 'fl live' : 'fl'}>
      <span
        className="dot"
        style={{ background: live ? 'var(--up)' : 'var(--down)' }}
      />
      {live ? 'Connected' : 'Offline'}
      {live && (
        <span className="mono" style={{ color: 'var(--t5)' }}>
          {latency}ms
        </span>
      )}
    </div>
  )
})

/**
 * P1/P2/P3 — the trade-setting presets, which turn out to be a feature the
 * backend has had all along: each preset carries its own slippage, priority fee
 * and quick-buy amounts, and the server remembers which one is active. So this
 * is a switch over something real, not three buttons that store a number here.
 */
const PresetSwitch: React.FC = memo(() => {
  const [presetsState] = useAtom(tradeSettingsModel.networkSettingsPresetsState)
  const [activeIndex] = useAtom(activePresetIndexAtom)
  const setActivePreset = useAction(tradeSettingsModel.setActivePreset)

  const presets = AsyncStateN.matchWaiting(presetsState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (value) => value.presets,
  })

  if (!presets || presets.length === 0) {
    return undefined
  }

  return (
    <div className="seg" style={{ height: 22, padding: 1 }}>
      {presets.map((preset, index) => (
        <button
          key={preset.index}
          className={index === activeIndex ? 'on' : undefined}
          type="button"
          style={{ height: 18 }}
          /* The preset's own name is what the user called it; the label stays
             short so the strip does not grow with it. */
          title={`${preset.emoji} ${preset.name}`.trim()}
          onClick={() => setActivePreset(index)}
        >
          P{index + 1}
        </button>
      ))}
    </div>
  )
})

/** What this account can spend, in whichever place its mode keeps money. */
const Held: React.FC = memo(() => {
  const spendable = useSpendable()

  return (
    <div className="fl">
      Held{' '}
      <span className="mono" style={{ color: 'var(--t1)' }}>
        {spendable ?? '—'}
      </span>
    </div>
  )
})

const Slippage: React.FC = memo(() => {
  const settings = useActiveSettings()

  return (
    <div className="fl">
      Slippage{' '}
      <span className="mono" style={{ color: 'var(--t1)' }}>
        {settings ? `${settings.slippage}%` : '—'}
      </span>
    </div>
  )
})

const Priority: React.FC = memo(() => {
  const settings = useActiveSettings()

  return (
    <div className="fl drop">
      Priority{' '}
      <span className="mono" style={{ color: 'var(--t1)' }}>
        {/* Only some networks charge one, so the field is not on every
            branch of the settings union. */}
        {settings && 'priorityFee' in settings ? settings.priorityFee : '—'}
      </span>
    </div>
  )
})

/** The active preset's settings, or nothing while they are still coming. */
const useActiveSettings = () => {
  const [settingsState] = useAtom(tradeSettingsStateAtom)

  return AsyncStateN.matchWaiting(settingsState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (value) => value,
  })
}
